<template>
  <MdModal
    v-if="isModalOpen"
    id="contract-details-modal"
    class="contractDetailsModal"
    :showoverlay="true"
    :isopen="isModalOpen"
    width="776px"
    :enablebackdropclick="false"
    data-qa-id="contract-details-modal"
    @handleclose="closeModal"
  >
    <div slot="header" class="contractDetails__header">
      <MdTypography
        id="contract-modal-title"
        tag="h5"
        textalign="start"
        color="spirits--900"
        variant="heading5--medium"
        data-qa-id="contract-details-modal-title"
      >
        {{ $t('pages.inventory_ingestion_hub.contract_details_modal.title') }}
      </MdTypography>
    </div>

    <div slot="body" class="contractDetails__body">
      <!-- Server Error State -->
      <div v-if="hasServerError" class="no-results">
        <NoResultFound
          :iconToShow="ErrorImage"
          :imgHeight="240"
          :imgWidth="240"
          :noResultsTitle="$t('pages.inventory_ingestion_hub.contract_details_modal.api_error_title')"
          :noResultsContent="$t('pages.inventory_ingestion_hub.contract_details_modal.api_error_content')"
        />
      </div>
      <!-- No Results State -->
      <div v-else-if="!validContracts.length" class="no-results">
        <NoResultFound
          :iconToShow="NoDataCreated"
          :imgHeight="240"
          :imgWidth="240"
          :noResultsTitle="$t('pages.inventory_ingestion_hub.contract_details_modal.no_results_found_title')"
          :noResultsContent="$t('pages.inventory_ingestion_hub.contract_details_modal.no_results_found_content')"
        />
      </div>
      <agGrid
        v-else
        :rowData="validContracts"
        :columnDefs="columnDefs"
        :isPagination="false"
        :enableCellTextSelection="true"
        noRowsToShowText=""
        :class="{ 'has-error-banner': invalidContracts.length > 0 }"
      >
      </agGrid>

      <!-- Invalid Contracts Banner -->
      <div v-if="invalidContracts.length > 0" class="contractDetails__error-banner">
        <MdTypography
          tag="p"
          variant="body2--regular"
          color="red-babe--600"
          data-qa-id="contract-details-invalid-contracts"
        >
          {{ $t('pages.inventory_ingestion_hub.contract_details_modal.invalid_contracts_found', { contracts: invalidContracts.map(c => `'${c}'`).join(' , ') }) }}
        </MdTypography>
      </div>
    </div>
    <div slot="footer" class="contractDetails__footer">
      <MdButton
        isfullwidth="false"
        size="large"
        textalign="center"
        color="lightgrey"
        type="button"
        tabidx="2"
        variant="none"
        data-qa-id="contract-details-close-btn"
        @click="closeModal"
      >
        {{ $t('pages.inventory_ingestion_hub.edit_display_info_table.close') }}
      </MdButton>
    </div>
  </MdModal>
</template>

<script setup>
import { computed, onMounted, onUnmounted } from 'vue';
import { MdModal, MdButton, MdTypography } from '@antiqua-dls/vue-components';
import agGrid from 'components/agGrid/index.vue';
import { disableScrollOnModalOpen } from 'src/utils/helper/misc';
import NoResultFound from 'src/components/general/no-result-found/index.vue';
import NoDataCreated from 'src/components/icons/no-result-icon/no-data-created.svg';
import ErrorImage from 'src/pages/error-pages/error-svg/error.svg';

// Define props
const props = defineProps({
  isModalOpen: {
    type: Boolean,
    required: true
  },
  rowData: {
    type: Array,
    default: () => []
  },
  columnDefs: {
    type: Array,
    required: true
  },
  hasServerError: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['closeModal']);

// Computed property to get valid contracts for the table
const validContracts = computed(() => {
  return props.rowData.filter(contract => contract.isValid === true);
});

// Computed property to get invalid contracts for the error banner
const invalidContracts = computed(() => {
  return props.rowData.filter(contract => contract.isValid === false).map(contract => contract.contractNumber);
});

const closeModal = () => {
  emit('closeModal');
};

onMounted(() => {
  if (props.isModalOpen) {
    disableScrollOnModalOpen(true);
  }
});

onUnmounted(() => {
  disableScrollOnModalOpen(false);
});
</script>

<style scoped lang="scss">
.contractDetailsModal {
  &::part(md-modal__content) {
    gap: 0;
  }

  &::part(md-modal__header) {
    padding-bottom: var(--md-spacing-unit--lg);
  }

  &::part(md-modal__body) {
    padding-top: var(--md-spacing-unit--lg);
    padding-bottom: var(--md-spacing-unit--lg);
    border-top: 1px solid var(--md-color--spirits--300);
    border-bottom: 1px solid var(--md-color--spirits--300);
  }

  &::part(md-modal__footer) {
    padding-top: var(--md-spacing-unit--lg);
  }

  .contractDetails__body {
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
    gap: var(--md-spacing-unit--md);

    .ag-theme-overrides {
      padding: 0;
      width: 100%;

      &.has-error-banner {
        padding-bottom: var(--md-spacing-unit--3xl);
      }
    }

    .contractDetails__error-banner {
      background-color: var(--md-color--red-babe--50);
      border: 1px solid var(--md-color--spirits--300);
      position: absolute;
      width: 100%;
      height: 3.5rem;
      padding: 0 var(--md-spacing-unit--2xl);
      align-content: center;
      bottom: 7.063rem;
    }

    .no-results {
      display: flex;
      align-self: center;
      color: var(--md-color--spirits--700);
      padding: var(--md-spacing-unit--md) 0 var(--md-spacing-unit--xl) 0;
      width: 100%;
      justify-content: center;
      height: 25rem;
      align-items: center;
    }
  }
}
</style>
